import React from "react";
import { Search, X } from "lucide-react";
import type { AdminFilterTab } from "../hooks/useAdminProductsList";

const TABS: { key: AdminFilterTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "baskets", label: "Baskets" },
  { key: "portfolios", label: "Portfolios" },
  { key: "managedStrategies", label: "Managed Strategies" },
];

const VISIBILITY_OPTIONS = ["PUBLIC", "PRIVATE", "SUBSCRIBER"] as const;

interface AdminProductFiltersProps {
  filterTab: AdminFilterTab;
  onFilterTabChange: (tab: AdminFilterTab) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  providerFilter: number | null;
  onProviderFilterChange: (id: number | null) => void;
  visibilityFilter: string | null;
  onVisibilityFilterChange: (v: string | null) => void;
  remarksFilter: string;
  onRemarksFilterChange: (value: string) => void;
  providers: { id: number; name: string }[];
}

const AdminProductFilters: React.FC<AdminProductFiltersProps> = ({
  filterTab,
  onFilterTabChange,
  searchTerm,
  onSearchChange,
  providerFilter,
  onProviderFilterChange,
  visibilityFilter,
  onVisibilityFilterChange,
  remarksFilter,
  onRemarksFilterChange,
  providers,
}) => {
  const hasActiveFilters =
    providerFilter !== null ||
    visibilityFilter !== null ||
    searchTerm !== "" ||
    remarksFilter !== "";

  return (
    <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
      <div className="flex items-center gap-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => onFilterTabChange(tab.key)}
            className={`text-[11px] font-bold px-4 py-1.5 rounded-lg uppercase tracking-wide transition-all ${
              filterTab === tab.key
                ? "text-rp-grey bg-rp-amber/10 border border-rp-amber/20"
                : "text-rp-grey/60 hover:text-rp-grey hover:bg-rp-separator"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Search */}
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by name, provider, or ID..."
            className="bg-rp-surface border border-rp-separator rounded-lg pl-10 pr-8 py-1.5 text-sm w-full focus:outline-none focus:border-rp-amber transition-all placeholder-rp-grey/40 text-rp-grey/80"
          />
          <Search className="absolute left-3 top-2.5 text-rp-grey/50 w-4 h-4" />
          {searchTerm && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-2 text-rp-grey/50 hover:text-rp-grey"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Provider filter */}
        <select
          value={providerFilter ?? ""}
          onChange={(e) => onProviderFilterChange(e.target.value ? Number(e.target.value) : null)}
          className="bg-rp-surface border border-rp-separator rounded-lg px-3 py-1.5 text-sm text-rp-grey/80 focus:outline-none focus:border-rp-amber transition-all"
        >
          <option value="">All Providers</option>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        {/* Visibility filter */}
        <select
          value={visibilityFilter ?? ""}
          onChange={(e) => onVisibilityFilterChange(e.target.value || null)}
          className="bg-rp-surface border border-rp-separator rounded-lg px-3 py-1.5 text-sm text-rp-grey/80 focus:outline-none focus:border-rp-amber transition-all"
        >
          <option value="">All Visibility</option>
          {VISIBILITY_OPTIONS.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>

        {/* Contract remarks filter -- matches a product when ANY comma-separated
            token appears in one of its remarks. */}
        <div className="relative w-full sm:w-56">
          <input
            type="text"
            value={remarksFilter}
            onChange={(e) => onRemarksFilterChange(e.target.value)}
            aria-label="Filter by contract remarks"
            title="Comma-separated remarks; shows products matching any of them"
            placeholder="Remarks (comma separated)..."
            className="bg-rp-surface border border-rp-separator rounded-lg pl-3 pr-8 py-1.5 text-sm w-full focus:outline-none focus:border-rp-amber transition-all placeholder-rp-grey/40 text-rp-grey/80"
          />
          {remarksFilter && (
            <button
              onClick={() => onRemarksFilterChange("")}
              aria-label="Clear remarks filter"
              className="absolute right-2 top-2 text-rp-grey/50 hover:text-rp-grey"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Clear all */}
        {hasActiveFilters && (
          <button
            onClick={() => {
              onSearchChange("");
              onProviderFilterChange(null);
              onVisibilityFilterChange(null);
              onRemarksFilterChange("");
              onFilterTabChange("all");
            }}
            className="text-rp-amber hover:text-rp-amber text-sm font-medium"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminProductFilters;
